import { gql } from "config/graphql";
import {
  BusinessSlugTypes,
  IConstellation,
  IMembership,
  IPhoneNumber,
  IUser,
  MutationResponse,
} from "config/types";
import { FEATURE_FLAG_FIELDS } from "gql/fragments";
import { getApolloClient } from "store/apollo";
import { assertDefined } from "utils/error";

// Register user
export interface IRegisterUserInput {
  attributes: {
    emailAddress: string;
    firstName: string;
    lastName: string;
    password: string;
    passwordConfirmation: string;
    newsletterOptIn: boolean;
    insuranceMembershipId?: IMembership["id"];
  };
}

interface IRegisterUserData {
  registerUser: {
    user: {
      id: string;
    };
  };
}

export const registerUserMutation: MutationResponse<IRegisterUserInput, IRegisterUserData> = async (
  user: IRegisterUserInput
) => {
  const client = getApolloClient();

  const mutation = gql`
    mutation registerUser($input: RegisterUserInput!) {
      registerUser(input: $input) {
        clientMutationId
        user {
          id
        }
      }
    }
  `;

  const { data } = await client.mutate<IRegisterUserData>({
    variables: { input: user },
    mutation,
  });
  return { data: assertDefined(data) };
};

// Create phone number

export interface ICreatePhoneNumberInput {
  phoneNumber: string;
  countryCode: string;
  smsMarketing?: boolean;
}

interface ICreatePhoneNumberData {
  createPhoneNumber: {
    phoneNumber: IPhoneNumber;
  };
}

export const createPhoneNumberMutation: MutationResponse<
  ICreatePhoneNumberInput,
  ICreatePhoneNumberData
> = async (phoneNumber: ICreatePhoneNumberInput) => {
  const client = getApolloClient();

  const mutation = gql`
    mutation createPhoneNumber($input: CreatePhoneNumberInput!) {
      createPhoneNumber(input: $input) {
        phoneNumber {
          id
          mask
          status
          createdAt
        }
      }
    }
  `;

  const { data } = await client.mutate<ICreatePhoneNumberData>({
    variables: { input: phoneNumber },
    mutation,
  });
  return { data: assertDefined(data) };
};

interface IDecommissionUserData {
  decommissionUser: {
    response: {
      success: boolean;
    };
  };
}

export const decommissionUserMutation = async (): Promise<{ data: IDecommissionUserData }> => {
  const client = getApolloClient();

  const mutation = gql`
    mutation decommissionUser($input: DecommissionUserInput!) {
      decommissionUser(input: $input) {
        response {
          success
        }
      }
    }
  `;

  const { data } = await client.mutate<IDecommissionUserData>({
    mutation,
  });
  return { data: assertDefined(data) };
};

export interface ISetupUserForNewLineOfBusinessInput {
  businessSlug: BusinessSlugTypes;
  constellationId?: IConstellation["id"];
  userId?: IUser["id"];
}

interface ISetupUserForNewLineOfBusinessData {
  setupUserForNewLineOfBusiness: {
    user: Pick<IUser, "activeConstellations" | "id" | "status">;
  };
}

export const setUpUserForNewLineOfBusinessMutation: MutationResponse<
  ISetupUserForNewLineOfBusinessInput,
  ISetupUserForNewLineOfBusinessData
> = async (input: ISetupUserForNewLineOfBusinessInput) => {
  const client = getApolloClient();

  const mutation = gql`
    ${FEATURE_FLAG_FIELDS}
    mutation SetupUserForNewLineOfBusiness($input: SetupUserForNewLineOfBusinessInput!) {
      setupUserForNewLineOfBusiness(input: $input) {
        user {
          activeConstellations {
            activeInsuranceMembership {
              id
            }
            id
            featureFlags {
              ...FeatureFlagFields
            }
          }
          id
          status
        }
      }
    }
  `;

  const { data } = await client.mutate<ISetupUserForNewLineOfBusinessData>({
    mutation,
    variables: { input },
  });
  return { data: assertDefined(data) };
};
